import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Check, Hotel, LogOut, User } from "lucide-react";
import type { RootState } from "@/state/store";
import type { HotelMeta } from "@/models/api.models";
import { userSignOut } from "@/App/auth/auth";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";

const logoURL = `assets/images/${import.meta.env.VITE_REACT_APP_STAGE}/revanista-logo.png`;

const getHotelLabel = (hotel: HotelMeta) =>
  hotel.info?.name || hotel.name || String(hotel.hotelId);

const AppHeader = ({ username }: { username: string }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const hotels = useSelector(
    (state: RootState) => state.hotelData.hotels,
  ) as HotelMeta[];
  const [hotelPickerOpen, setHotelPickerOpen] = useState(false);
  const [query, setQuery] = useState("");

  const currentHotelId = useMemo(
    () => location.pathname.split("/").filter(Boolean)[0] ?? "",
    [location.pathname],
  );

  const filteredHotels = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return hotels;
    return hotels.filter((hotel) =>
      getHotelLabel(hotel).toLowerCase().includes(term),
    );
  }, [hotels, query]);

  const pickHotel = (hotelId: string) => {
    setHotelPickerOpen(false);
    setQuery("");
    navigate(`/${hotelId}`);
  };

  return (
    <header className="sticky top-0 z-20 flex h-14 items-center justify-between gap-3 border-b bg-white px-3 shadow-sm sm:px-6">
      <img src={logoURL} alt="Revanista" className="h-10 w-auto shrink-0" />

      <div className="flex items-center gap-1">
        {hotels.length > 0 && (
          <>
            <Button
              size="icon"
              variant="ghost"
              onClick={() => setHotelPickerOpen(true)}
              aria-label="Change hotel"
              title="Change hotel"
            >
              <Hotel className="h-5 w-5" />
            </Button>
            <Dialog open={hotelPickerOpen} onOpenChange={setHotelPickerOpen}>
              <DialogContent className="w-[92vw] sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>Select hotel</DialogTitle>
                </DialogHeader>
                <Input
                  placeholder="Search hotels"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  autoFocus
                />
                <div className="max-h-[50vh] overflow-y-auto">
                  {filteredHotels.length === 0 ? (
                    <p className="py-6 text-center text-sm text-muted-foreground">
                      No hotels match your search.
                    </p>
                  ) : (
                    filteredHotels.map((hotel) => {
                      const id = String(hotel.hotelId);
                      const selected = id === currentHotelId;
                      return (
                        <button
                          key={id}
                          type="button"
                          onClick={() => pickHotel(id)}
                          className={`flex w-full items-center justify-between gap-2 rounded-md px-3 py-3 text-left text-sm hover:bg-muted/50 ${
                            selected ? "bg-muted/40 font-medium" : ""
                          }`}
                        >
                          <span className="truncate">
                            {getHotelLabel(hotel)}
                          </span>
                          {selected && (
                            <Check className="h-4 w-4 shrink-0 text-primary" />
                          )}
                        </button>
                      );
                    })
                  )}
                </div>
              </DialogContent>
            </Dialog>
          </>
        )}

        <DropdownMenu>
          <DropdownMenuTrigger
            className="outline-none"
            aria-label="Account menu"
          >
            <Avatar className="h-8 w-8">
              <AvatarFallback>
                <User className="h-4 w-4" />
              </AvatarFallback>
            </Avatar>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {username && (
              <div className="max-w-56 truncate px-2 py-1.5 text-xs text-muted-foreground">
                {username}
              </div>
            )}
            <DropdownMenuItem onClick={() => void userSignOut()}>
              <LogOut className="mr-2 h-4 w-4" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};

export default AppHeader;
